import { useEffect, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent } from 'react';
import { supabase } from '../lib/supabase';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Send, Loader2, Users, GraduationCap, FileCode2, Bot, Settings as SettingsIcon, Download } from 'lucide-react';
import { motion, LayoutGroup, useMotionValue, useSpring, useReducedMotion, type HTMLMotionProps } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

// Types
interface BaseItem {
  id: string;
  title: string;
  description: string | null;
}

interface ClassItem extends BaseItem {
  video_url?: string | null;
  thumbnail?: string | null;
  class_categories?: { category_id: string }[];
}

interface BlueprintItem extends BaseItem {
  download_url?: string | null;
  logo_url?: string | null;
}

interface ToolItem extends BaseItem {
  tool_url?: string | null;
  logo_url?: string | null;
}

type CardKind = 'class' | 'blueprint' | 'tool';

interface RecommendationCard {
  kind: CardKind;
  id: string;
  title: string;
  description: string | null;
  link: string;
  tip: string;
  video_url?: string | null;
  logo_url?: string | null;
  thumbnail?: string | null;
}

type ChatMessage = {
  role: 'user' | 'assistant' | 'system';
  content: string;
  cards?: RecommendationCard[];
  generalTip?: string;
};

// Local storage key for persisting chat history in the browser
const STORAGE_KEY = 'rag_agent_messages_v1';

// (keyword search removed; fetching full catalog instead)

// Magnetic cursor effect
function useMagnetic(radius = 140, max = 16) {
  const prefersReducedMotion = useReducedMotion();
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const springX = useSpring(x, { stiffness: 320, damping: 22, mass: 0.4 });
  const springY = useSpring(y, { stiffness: 320, damping: 22, mass: 0.4 });

  const onMouseMove = (e: ReactMouseEvent<HTMLElement>) => {
    if (prefersReducedMotion) return;
    const target = e.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const dx = e.clientX - cx;
    const dy = e.clientY - cy;
    const dist = Math.hypot(dx, dy);
    if (dist < radius) {
      const f = 1 - dist / radius; // 0..1
      const tx = Math.max(-max, Math.min(max, (dx / radius) * max)) * f;
      const ty = Math.max(-max, Math.min(max, (dy / radius) * max)) * f;
      x.set(tx);
      y.set(ty);
    } else {
      x.set(0);
      y.set(0);
    }
  };

  const onMouseLeave = () => {
    x.set(0);
    y.set(0);
  };

  return {
    style: prefersReducedMotion ? undefined : { x: springX, y: springY },
    onMouseMove: prefersReducedMotion ? undefined : onMouseMove,
    onMouseLeave: prefersReducedMotion ? undefined : onMouseLeave,
  } as const;
}

function MagneticButton({ children, className = '', onClick }: { children: React.ReactNode; className?: string; onClick?: () => void }) {
  const mag = useMagnetic(80, 8);
  return (
    <motion.button
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      className={className}
      {...mag}
    >
      {children}
    </motion.button>
  );
}

// Generic magnetic motion wrapper so hooks aren't called conditionally in parent
type MagneticMotionProps = HTMLMotionProps<'div'> & { radius?: number; max?: number };
function MagneticMotion({ radius = 140, max = 16, children, ...rest }: MagneticMotionProps) {
  const mag = useMagnetic(radius, max);
  return (
    <motion.div {...rest} {...mag}>
      {children}
    </motion.div>
  );
}

// (extractKeywords removed)

// (scoring helpers removed; Gemini handles ranking)

function buildLinkForClass(c: ClassItem): string {
  const folderId = c.class_categories?.[0]?.category_id;
  // Prefer direct link to the class details page, preserving folder context when available
  return folderId
    ? `/classroom/${encodeURIComponent(c.id)}?folder=${encodeURIComponent(folderId)}`
    : `/classroom/${encodeURIComponent(c.id)}`;
}

function buildLinkForBlueprint(b: BlueprintItem): string {
  return b.download_url || '/blueprints';
}

function buildLinkForTool(t: ToolItem): string {
  return t.tool_url || '#';
}

// Convert various YouTube URL forms to an embeddable URL
function toYouTubeEmbed(url?: string | null): string | null {
  if (!url) return null;
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, '');
    // youtu.be/<id>
    if (host === 'youtu.be') {
      const id = u.pathname.split('/').filter(Boolean)[0];
      return id ? `https://www.youtube.com/embed/${id}` : null;
    }
    // youtube.com/shorts/<id>
    if (host.endsWith('youtube.com') && u.pathname.startsWith('/shorts/')) {
      const id = u.pathname.split('/').filter(Boolean)[1];
      return id ? `https://www.youtube.com/embed/${id}` : null;
    }
    // youtube.com/watch?v=<id>
    if (host.endsWith('youtube.com') && (u.pathname === '/watch' || u.pathname === '/watch/')) {
      const id = u.searchParams.get('v');
      return id ? `https://www.youtube.com/embed/${id}` : null;
    }
    return null;
  } catch {
    return null;
  }
}

// (removed shorten helper; no longer needed since descriptions/tips are hidden for blueprints/tools)

function extractJsonBlock(text: string): unknown | null {
  // Try fenced ```json blocks first
  const fence = text.match(/```json\s*([\s\S]*?)```/i);
  if (fence) {
    try { return JSON.parse(fence[1]); } catch { /* noop */ void 0; }
  }
  // Fallback: any fenced code
  const anyFence = text.match(/```\s*([\s\S]*?)```/);
  if (anyFence) {
    try { return JSON.parse(anyFence[1]); } catch { /* noop */ void 0; }
  }
  // Last resort: find first {...} blob
  const start = text.indexOf('{');
  const end = text.lastIndexOf('}');
  if (start !== -1 && end !== -1 && end > start) {
    const slice = text.slice(start, end + 1);
    try { return JSON.parse(slice); } catch { /* noop */ void 0; }
  }
  return null;
}

function coerceCardsFromLLM(json: unknown): { cards: RecommendationCard[]; generalTip?: string } {
  const out: RecommendationCard[] = [];
  let generalTip: string | undefined;
  try {
    const obj = (typeof json === 'object' && json !== null) ? (json as Record<string, unknown>) : {};
    const cardsVal = (obj as { cards?: unknown }).cards;
    const arr: unknown[] = Array.isArray(cardsVal) ? cardsVal as unknown[] : [];
    for (const c of arr) {
      if (!c || typeof c !== 'object') continue;
      const rec = c as Record<string, unknown>;
      const id = rec.id;
      const title = rec.title;
      const kindRaw = rec.kind;
      if (typeof id === 'undefined' || typeof title !== 'string' || typeof kindRaw !== 'string') continue;
      const kind: CardKind = kindRaw === 'class' || kindRaw === 'blueprint' || kindRaw === 'tool' ? kindRaw : 'tool';
      out.push({
        kind,
        id: String(id),
        title: title,
        description: typeof rec.description === 'string' ? rec.description : null,
        link: String(rec.link ?? ''),
        tip: typeof rec.tip === 'string' ? rec.tip : '',
        video_url: typeof rec.video_url === 'string' ? rec.video_url : null,
        logo_url: typeof rec.logo_url === 'string' ? rec.logo_url : null,
        thumbnail: typeof rec.thumbnail === 'string' ? rec.thumbnail : null,
      });
    }
    const tipVal = (obj as { generalTip?: unknown }).generalTip;
    if (typeof tipVal === 'string') generalTip = tipVal;
  } catch { /* noop */ void 0; }
  return { cards: out, generalTip };
}
async function queryTable<T extends BaseItem>(
  table: string,
  keywords: string[],
  select = '*',
  limit = 10
): Promise<T[]> {
  if (keywords.length === 0) {
    const { data, error } = await supabase.from(table).select(select).limit(limit);
    if (error) throw error;
    return (data as T[]) || [];
  }

  const ors = keywords
    .map(k => `title.ilike.%${k}%,description.ilike.%${k}%`)
    .join(',');

  const { data, error } = await supabase
    .from(table)
    .select(select)
    .or(ors)
    .limit(limit);

  if (error) throw error;
  return (data as T[]) || [];
}

async function callGeminiLLM(
  prompt: string,
  apiKey: string,
  model: string
): Promise<string> {
  // Use REST to avoid adding new deps
  const url = `https://generativelanguage.googleapis.com/v1beta/models/${encodeURIComponent(model)}:generateContent?key=${encodeURIComponent(apiKey)}`;
  const body = {
    contents: [
      {
        role: 'user',
        parts: [{ text: prompt }]
      }
    ]
  };

  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  });

  if (!res.ok) {
    const txt = await res.text();
    throw new Error(`Gemini error ${res.status}: ${txt}`);
  }
  const json = await res.json();
  const text = json?.candidates?.[0]?.content?.parts?.[0]?.text;
  if (!text) throw new Error('No response from Gemini');
  return text as string;
}

export default function RagAgent() {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [systemPrompt, setSystemPrompt] = useState<string | null>(null);

  const geminiApiKey = import.meta.env.VITE_GEMINI_API_KEY as string | undefined;
  const geminiModel = (import.meta.env.VITE_GEMINI_MODEL as string | undefined) || 'gemini-2.5-pro';

  const containerRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const navigate = useNavigate();
  // Controls hero tile animation: 'idle' (visible) -> 'out' (slide left & disappear)
  const [tilePhase, setTilePhase] = useState<'idle' | 'out'>('idle');
  // Keep the hero container mounted a bit after chat starts so tiles can animate out
  const [heroVisible, setHeroVisible] = useState(true);
  // Control visibility of central brand+input separately from tiles
  const [heroBrandVisible, setHeroBrandVisible] = useState(true);
  // When textarea grows to multiple lines, relax the rounding from full pill to 2xl
  const [inputTall, setInputTall] = useState(false);

  // Auto-resize textarea height to fit content for a smooth expanding border
  const autoResize = (el: HTMLTextAreaElement | null = textareaRef.current) => {
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
    // Mark input as tall when content exceeds ~2 lines height
    setInputTall(el.scrollHeight > 80);
  };

  useEffect(() => {
    // ensure rounding is correct on first paint
    autoResize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearChat = () => {
    setMessages([]);
    setInput('');
    setError(null);
    setTilePhase('idle');
    setHeroBrandVisible(true);
    setHeroVisible(true);
    setInputTall(false);
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* noop */ void 0; }
    try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch { /* noop */ void 0; }
  };

  // Always reset any persisted chat on mount so reload/back starts with a clean slate
  useEffect(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      console.warn('Failed to reset chat history on mount:', e);
    }
  }, []);

  // Clear chat storage on unmount/reload/back-forward cache restore
  useEffect(() => {
    const clearStorage = () => {
      try { localStorage.removeItem(STORAGE_KEY); } catch { /* noop */ void 0; }
    };

    const handleBeforeUnload = () => {
      // Clear persisted chat on full reload/close
      clearStorage();
    };
    const handlePageHide = () => {
      // Safari/iOS fires pagehide for BFCache; clear just in case
      clearStorage();
    };
    const handlePageShow = (e: any) => {
      // If coming back from BFCache, reset storage and UI to a clean state
      if (e && e.persisted) {
        clearStorage();
        setMessages([]);
        setTilePhase('idle');
        setHeroBrandVisible(true);
        setHeroVisible(true);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('pageshow', handlePageShow);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('pageshow', handlePageShow);
      // Also clear when navigating away from the Agent route
      clearStorage();
    };
  }, []);

  // Persist chat to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(messages));
    } catch (e) {
      console.warn('Failed to persist chat history to localStorage:', e);
    }
  }, [messages]);

  // Fetch the latest AI system prompt from Supabase on mount
  useEffect(() => {
    let canceled = false;
    (async () => {
      try {
        const { data, error } = await supabase
          .from('ai_system_prompts')
          .select('prompt')
          .order('updated_at', { ascending: false })
          .limit(1)
          .single();
        if (error) throw error;
        if (!canceled) setSystemPrompt((data as { prompt: string }).prompt);
      } catch (e: unknown) {
        console.error('Failed to fetch system prompt:', e);
        setError(e instanceof Error ? e.message : 'Failed to load system prompt.');
      }
    })();
    return () => {
      canceled = true;
    };
  }, []);

  useEffect(() => {
    // Auto-scroll on new messages
    const el = containerRef.current;
    const scrollWindow = () => {
      try { window.scrollTo({ top: document.documentElement.scrollHeight, behavior: 'smooth' }); } catch { /* noop */ void 0; }
    };
    if (el) {
      const overflows = el.scrollHeight > el.clientHeight + 4; // small tolerance
      if (overflows) {
        try { el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' }); } catch { scrollWindow(); }
      } else {
        scrollWindow();
      }
    } else {
      scrollWindow();
    }
  }, [messages, loading]);

  // Keep textarea height in sync with content
  useEffect(() => {
    autoResize();
  }, [input]);

  // When switching from hero input to docked input, ensure height resets properly
  useEffect(() => {
    autoResize();
  }, [heroBrandVisible]);

  // AI-driven intent: we will always supply a catalog and let the model decide
  // whether to recommend now or ask for clarification (no code heuristics).

  const send = async () => {
    const q = input.trim();
    if (!q) return;
    setInput('');
    setError(null);

    // On send, if hero brand is currently visible, start transitions (covers reload/edge cases)
    if (heroBrandVisible) {
      setTilePhase('out');             // tiles slide out
      setHeroBrandVisible(false);      // immediately unmount center logo+input to enable shared layout morphs
      setTimeout(() => setHeroVisible(false), 850); // then remove tile container after stagger completes
    }

    const userMsg: ChatMessage = { role: 'user', content: q };
    setMessages((prev: ChatMessage[]) => [...prev, userMsg]);

    // Build chat history (include previous turns + current user message)
    const conversationForLLM: ChatMessage[] = [...messages, userMsg];
    const historyText = conversationForLLM
      .slice(-12) // keep the last ~12 messages for prompt size
      .map(m => `${m.role === 'user' ? 'User' : 'Assistant'}: ${m.content}`)
      .join('\n');

    try {
      setLoading(true);
      // Fetch ALL resources and let the model recommend relevant workflows/resources
      const keywords: string[] = [];

      const [classes, blueprints, tools] = await Promise.all<[
        Promise<ClassItem[]>,
        Promise<BlueprintItem[]>,
        Promise<ToolItem[]>
      ]>([
        queryTable<ClassItem>('classes', keywords, 'id,title,description,video_url,thumbnail,class_categories(category_id)', 1000),
        queryTable<BlueprintItem>('blueprints', keywords, 'id,title,description,download_url,logo_url', 1000),
        queryTable<ToolItem>('ai_tools', keywords, 'id,title,description,tool_url,logo_url', 1000)
      ]);

      const classById = new Map(classes.map(c => [c.id, c]));
      const blueprintById = new Map(blueprints.map(b => [b.id, b]));
      const toolById = new Map(tools.map(t => [t.id, t]));

      const catalog = {
        query: q,
        keywords,
        classes: classes.map(c => ({ id: c.id, title: c.title, description: c.description, video_url: c.video_url, link: buildLinkForClass(c) })),
        blueprints: blueprints.map(b => ({ id: b.id, title: b.title, description: b.description, download_url: b.download_url, link: buildLinkForBlueprint(b) })),
        tools: tools.map(t => ({ id: t.id, title: t.title, description: t.description, tool_url: t.tool_url, link: buildLinkForTool(t) }))
      };

      const systemStyle = `${systemPrompt ?? ''}

Decide intelligently whether to recommend now or ask a clarifying question.
- If the user's intent is sufficiently clear (for example, they stated a business domain like "roofing" and a concrete goal like "lead generation"), then recommend relevant resources.
- If intent is unclear, return no cards and ask one short clarifying question.

From the provided CATALOG, choose up to 3 Classes, 3 Blueprints, and 3 Tools that best match the user's latest query and prior chat history. Respond ONLY with JSON using this schema:

{
  "cards": [
    {
      "kind": "class" | "blueprint" | "tool",
      "id": "<id from catalog>",
      "title": "...",
      "description": "...",
      "link": "<exact link from catalog>",
      "tip": "one short tip to use it effectively",
      "video_url": "<video_url from catalog>" // only for kind=="class"
    }
  ],
  "generalTip": "one short helpful answer or, if not recommending, a clarifying question"
}

Rules:
- Use ONLY items from the CATALOG; never invent IDs or links.
- Prefer items that strongly match the user's stated goal and domain (use history context, e.g., industry + problem like roofing + lead generation).
- Recommend only our vetted Tools (i.e., those present in the CATALOG 'tools'). If the user names a tool not in catalog, suggest the closest vetted alternative from the CATALOG.
- Keep each tip concise (<= 120 chars) and use it to explain why the resource helps the user.
- If intent is unclear, set cards: [] and use generalTip to ask a single clarifying question.`;

      const prompt = `SYSTEM\n${systemStyle}\n\nCHAT HISTORY\n${historyText}\n\nUSER QUERY\n${q}\n\nCATALOG\n${'```'}json\n${JSON.stringify(catalog, null, 2)}\n${'```'}\n\nTASK\nReturn JSON only (no prose).`;

      const llmText = await callGeminiLLM(prompt, geminiApiKey!, geminiModel);
      const parsed = extractJsonBlock(llmText) || {};
      const coerced = coerceCardsFromLLM(parsed);

      const perKindCount: Record<CardKind, number> = { class: 0, blueprint: 0, tool: 0 };
      const finalCards: RecommendationCard[] = [];
      for (const c of (coerced.cards || [])) {
        if (!['class','blueprint','tool'].includes(c.kind)) continue;
        if (perKindCount[c.kind as CardKind] >= 3) continue;
        let card: RecommendationCard | null = null;
        if (c.kind === 'class') {
          const src = classById.get(c.id);
          if (!src) continue;
          card = {
            kind: 'class', id: src.id, title: src.title, description: src.description,
            link: buildLinkForClass(src), tip: c.tip || '', video_url: src.video_url || null, thumbnail: src.thumbnail || null
          };
        } else if (c.kind === 'blueprint') {
          const src = blueprintById.get(c.id);
          if (!src) continue;
          card = {
            kind: 'blueprint', id: src.id, title: src.title, description: src.description,
            link: buildLinkForBlueprint(src), tip: c.tip || '', logo_url: src.logo_url || null
          } as RecommendationCard;
        } else if (c.kind === 'tool') {
          const src = toolById.get(c.id);
          if (!src) continue;
          card = {
            kind: 'tool', id: src.id, title: src.title, description: src.description,
            link: buildLinkForTool(src), tip: c.tip || '', logo_url: src.logo_url || null
          } as RecommendationCard;
        }
        if (card) {
          finalCards.push(card);
          perKindCount[card.kind] = (perKindCount[card.kind] || 0) + 1;
        }
      }

      const hasRenderable = finalCards.length > 0 || !!coerced.generalTip;
      setMessages((prev: ChatMessage[]) => [
        ...prev,
        hasRenderable
          ? { role: 'assistant', content: '', cards: finalCards, generalTip: coerced.generalTip }
          : { role: 'assistant', content: llmText }
      ]);
    } catch (e: unknown) {
      console.error(e);
      setError(e instanceof Error ? e.message : 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  };

  const tiles = useMemo(() => ([
    { label: 'Community', icon: Users, to: '/community' },
    { label: 'Classes', icon: GraduationCap, to: '/classroom' },
    { label: 'Blueprints', icon: FileCode2, to: '/blueprints' },
    { label: 'Tools', icon: Bot, to: '/ai-tool' },
  ]), []);

  const hasStarted = messages.length > 0 || !heroBrandVisible;

  return (
    <LayoutGroup>
      <div className="app-bg" aria-hidden="true" />
      <div className="relative z-10 flex flex-col min-h-[calc(100vh-80px)] p-4 md:p-8 font-mont">
      {/* HERO: Centered logo + input & tiles (kept visible briefly after start for smooth tile exit) */}
      {heroVisible && (
        <div className="flex-1 flex flex-col items-center justify-center pt-8 md:pt-16 text-center">
          {/* Center logo + input (hidden immediately on first send to allow shared layout to the top logo and bottom dock) */}
          {heroBrandVisible && (
            <>
              {/* Magnetic only on the logo */}
              <MagneticMotion radius={180} max={14} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
                <motion.div layoutId="brand" className="mx-auto w-16 h-16 md:w-20 md:h-20 rounded-full ring-1 ring-white/10 bg-gray-950/60 backdrop-blur flex items-center justify-center shadow-lg shadow-cyan-300/20">
                  <img
                    src="https://i.postimg.cc/25s922XT/image.png"
                    alt="Mentor AI Logo"
                    className="w-3/4 h-3/4 object-contain"
                  />
                </motion.div>
              </MagneticMotion>
              {/* Title is static (no magnetic) */}
              <motion.h1
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, delay: 0.05 }}
                className="mt-4 text-2xl md:text-4xl font-normal text-white tracking-tight"
              >
                Mentor Community AI Agent
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, delay: 0.1 }}
                className="mt-3 md:mt-4 text-[12px] md:text-base text-gray-300 max-w-3xl mx-auto leading-relaxed px-4 md:px-0"
              >
                This AI Agent is here to help you automate your business, and learn your way around building automations! You can talk to it any time and he will guide you to the right classes, tools and blueprints to solve (automate) your problem. And if you don’t know where to start just have a chat, he will guide you!
              </motion.p>
              <motion.div layoutId="chatInput" layout className={`w-full md:w-2/3 max-w-none mx-auto mt-8 glow-ring-box transition-all ${inputTall ? 'rounded-2xl' : 'rounded-full'}`}>
                <motion.div layout className={`relative flex items-center gap-2 bg-gray-900/60 ${inputTall ? 'rounded-2xl' : 'rounded-full'} p-2 md:p-3 border border-white/10 backdrop-blur-md shadow-lg transition-all`}>
                  <textarea
                    ref={textareaRef}
                    rows={1}
                    value={input}
                    onChange={(e) => { setInput(e.target.value); autoResize(e.currentTarget); }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        send();
                      }
                    }}
                    placeholder="Ask for recommendations..."
                    className={`flex-1 bg-transparent text-white ${inputTall ? 'rounded-2xl' : 'rounded-full'} px-5 py-1.5 md:py-2.5 outline-none placeholder:text-gray-500 resize-none overflow-hidden leading-6`}
                  />
                  <button
                    onClick={send}
                    className="p-2 md:p-2.5 text-[#c9fffc] hover:text-white disabled:opacity-40 self-center"
                    disabled={loading || !input.trim()}
                    aria-label="Send"
                    title="Send"
                  >
                    <Send className="w-5 h-5" />
                  </button>
                </motion.div>
              </motion.div>
            </>
          )}

          {/* Tiles positioned close to the input */}
          <div className="mt-3 md:mt-4 px-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-3 gap-y-2.5 md:gap-3 max-w-3xl mx-auto">
              {tiles.map(({ label, icon: Icon, to }, i) => (
                <motion.div
                  key={label}
                  initial={{ x: -800, opacity: 0 }}
                  animate={tilePhase === 'out' ? { x: -800, opacity: 0, filter: 'blur(2px)' } : { x: 0, opacity: 1, filter: 'blur(0px)' }}
                  transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: i * 0.08 }}
                >
                  <MagneticButton
                    onClick={() => navigate(to)}
                    className="group relative z-0 w-full rounded-full bg-gray-900/60 border border-cyan-400/10 backdrop-blur px-4 py-2 md:px-5 md:py-3 text-left text-white/90 hover:border-cyan-300/30 hover:bg-gray-900/70 shadow-xl shadow-black/30"
                  >
                    <div className="flex items-center gap-2 md:gap-3">
                      <Icon className="w-4 h-4 md:w-5 md:h-5 text-[#c9fffc]" />
                      <div className="font-semibold tracking-wide text-sm md:text-base">{label}</div>
                    </div>
                  </MagneticButton>
                </motion.div>
              ))}
            </div>
            <motion.div
              initial={{ x: -800, opacity: 0 }}
              animate={tilePhase === 'out' ? { x: -800, opacity: 0, filter: 'blur(2px)' } : { x: 0, opacity: 1, filter: 'blur(0px)' }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: tiles.length * 0.08 + 0.04 }}
              className="mt-3 text-center"
            >
              <button onClick={() => navigate('/settings')} className="inline-flex items-center gap-1 text-sm underline text-[#c9fffc] hover:text-white">
                <SettingsIcon className="w-4 h-4" /> Settings
              </button>
            </motion.div>
          </div>
        </div>
      )}

      {/* STARTED CHAT: feed and input docked below */}
      {hasStarted && !heroBrandVisible && (
        <>
          {/* Compact brand at top-left (morphs from hero) */}
          <div className="flex items-center gap-3 mb-12 md:mb-20">
            <MagneticMotion radius={120} max={8} className="flex items-center gap-3">
              <motion.div layoutId="brand" className="w-16 h-16 md:w-20 md:h-20 rounded-full ring-1 ring-white/10 bg-gray-950/60 backdrop-blur flex items-center justify-center shadow-md shadow-cyan-300/10">
                <img
                  src="https://i.postimg.cc/25s922XT/image.png"
                  alt="Mentor AI Logo"
                  className="w-12 h-12 md:w-14 md:h-14 object-contain"
                />
              </motion.div>
            </MagneticMotion>
          </div>
          {error && (
            <div className="bg-red-500/10 border border-red-500/20 text-red-300 rounded-lg p-3 mb-3">{error}</div>
          )}
          <div ref={containerRef} className="flex-1 px-1 md:px-2 pt-6 md:pt-10 space-y-4 pb-28 md:pb-32">
            {messages.map((m: ChatMessage, i: number) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                {m.role === 'assistant' && ((m.cards && m.cards.length > 0) || m.generalTip) ? (
                  <div className="max-w-[95%] md:max-w-[90%] lg:max-w-5xl space-y-4">
                    {/* General tip bubble ABOVE the cards */}
                    {m.generalTip && (
                      <div
                        className="bg-gray-900/60 text-gray-100 border border-white/10 backdrop-blur-md shadow-xl shadow-black/30 max-w-[90%] md:max-w-[70%] rounded-3xl px-5 py-4"
                        style={{ wordBreak: 'break-word' }}
                      >
                        <div className="prose prose-sm max-w-none">{m.generalTip}</div>
                      </div>
                    )}
                    {/* Classes row */}
                    {m.cards && m.cards.filter(c => c.kind === 'class').length > 0 && (
                      <>
                        {/* Mobile: horizontal slider with side fades */}
                        <div className="sm:hidden relative -mx-4 px-4">
                          <div className="flex gap-3 overflow-x-auto snap-x snap-mandatory pb-2 pr-8 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
                            {m.cards.filter(c => c.kind === 'class').map((c, idx) => (
                              <div
                                key={`class-${c.id}-${idx}`}
                                className="shrink-0 snap-center rounded-2xl text-gray-100 h-full flex flex-col"
                                style={{ width: 'min(340px, calc(100vw - 2rem))' }}
                              >
                                {/* Title on top */}
                                <div className="p-4">
                                  <div className="font-semibold text-white clamp-1 break-words">{c.title}</div>
                                </div>
                                {/* Video embed */}
                                {(() => {
                                  const embed = toYouTubeEmbed(c.video_url || '');
                                  return embed ? (
                                    <div className="glow-ring-box rounded-xl w-[80%] mx-auto">
                                      <div className="relative w-full aspect-video bg-black rounded-xl overflow-hidden" style={{ aspectRatio: '16 / 9' }}>
                                        <iframe
                                          className="absolute inset-0 w-full h-full"
                                          src={embed}
                                          title={c.title}
                                          frameBorder="0"
                                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                          allowFullScreen
                                          loading="lazy"
                                        />
                                      </div>
                                    </div>
                                  ) : null;
                                })()}
                                {/* CTA at bottom */}
                                <div className="p-4 mt-auto">
                                  <div className="flex items-center justify-end">
                                    <a href={c.link} target="_blank" rel="noopener noreferrer" className="shrink-0 underline text-[#c9fffc] hover:text-white text-sm">See full masterclass</a>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                          {/* side fades */}
                          <div className="pointer-events-none absolute inset-y-0 left-0 w-6 bg-gradient-to-r from-gray-950/80 to-transparent" />
                          <div className="pointer-events-none absolute inset-y-0 right-0 w-6 bg-gradient-to-l from-gray-950/80 to-transparent" />
                        </div>

                        {/* Desktop/tablet: original grid */}
                        <div className="hidden sm:grid grid-cols-3 gap-4">
                          {m.cards.filter(c => c.kind === 'class').map((c, idx) => (
                            <div key={`class-${c.id}-${idx}`} className="rounded-2xl text-gray-100 h-full flex flex-col">
                              {/* Title on top */}
                              <div className="p-4">
                                <div className="font-semibold text-white clamp-1 break-words">{c.title}</div>
                              </div>
                              {/* Video embed */}
                              {(() => {
                                const embed = toYouTubeEmbed(c.video_url || '');
                                return embed ? (
                                  <div className="glow-ring-box rounded-xl">
                                    <div className="relative w-full aspect-video bg-black rounded-xl overflow-hidden before:block before:pt-[56.25%] before:content-['']">
                                      <iframe
                                        className="absolute inset-0 w-full h-full"
                                        src={embed}
                                        title={c.title}
                                        frameBorder="0"
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                        allowFullScreen
                                        loading="lazy"
                                      />
                                    </div>
                                  </div>
                                ) : null;
                              })()}
                              {/* CTA at bottom */}
                              <div className="p-4 mt-auto">
                                <div className="flex items-center justify-end">
                                  <a href={c.link} target="_blank" rel="noopener noreferrer" className="shrink-0 underline text-[#c9fffc] hover:text-white text-sm">See full masterclass</a>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </>
                    )}

                    {/* Blueprints row */}
                    {m.cards && m.cards.filter(c => c.kind === 'blueprint').length > 0 && (
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        {m.cards.filter(c => c.kind === 'blueprint').map((c, idx) => (
                          <div key={`blueprint-${c.id}-${idx}`} className="rounded-2xl overflow-hidden bg-gray-900/60 text-gray-100 border border-white/10 backdrop-blur-md shadow-lg shadow-black/30 p-0 h-full flex flex-col">
                            <div className="p-4 flex flex-col h-full">
                              <div className="flex items-start justify-between gap-3">
                                <div className="flex items-start gap-3 min-w-0">
                                  {c.logo_url && (
                                    <img src={c.logo_url} alt={`${c.title} logo`} className="w-9 h-9 rounded-md object-contain bg-white/5 border border-white/10" />
                                  )}
                                  <div className="min-w-0">
                                    <div className="font-semibold text-white clamp-1 break-words">{c.title}</div>
                                  </div>
                                </div>
                                <a href={c.link} target="_blank" rel="noopener noreferrer" className="shrink-0 inline-flex items-center justify-center rounded-full p-2 bg-[#c9fffc] text-gray-900 hover:bg-[#a0fcf9] shadow-md" aria-label="Download blueprint" title="Download">
                                  <Download className="w-4 h-4" />
                                </a>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Tools row */}
                    {m.cards && m.cards.filter(c => c.kind === 'tool').length > 0 && (
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        {m.cards.filter(c => c.kind === 'tool').map((c, idx) => (
                          <div key={`tool-${c.id}-${idx}`} className="rounded-2xl overflow-hidden bg-gray-900/60 text-gray-100 border border-white/10 backdrop-blur-md shadow-lg shadow-black/30 p-0 h-full flex flex-col">
                            <div className="p-4 flex flex-col h-full">
                              <div className="flex items-start justify-between gap-3">
                                <div className="flex items-start gap-3 min-w-0">
                                  {c.logo_url && (
                                    <img src={c.logo_url} alt={`${c.title} logo`} className="w-9 h-9 rounded-md object-contain bg-white/5 border border-white/10" />
                                  )}
                                  <div className="min-w-0">
                                    <div className="font-semibold text-white clamp-1 break-words">{c.title}</div>
                                  </div>
                                </div>
                                <a href={c.link} target="_blank" rel="noopener noreferrer" className="shrink-0 bg-[#c9fffc] text-gray-900 rounded-full px-4 py-2 text-sm hover:bg-[#a0fcf9] shadow-md">Open tool</a>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {/* generalTip moved above */}
                  </div>
                ) : (
                  <div className={`${m.role === 'user' ? 'bg-[#c9fffc] text-gray-900 shadow-xl' : 'bg-gray-900/60 text-gray-100 border border-white/10 backdrop-blur-md'} max-w-[90%] md:max-w-[70%] rounded-3xl px-5 py-4 shadow-lg shadow-black/30`}
                       style={{ wordBreak: 'break-word' }}>
                    {m.role === 'assistant' ? (
                      <div className="prose prose-invert prose-sm max-w-none prose-a:text-[#c9fffc]">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content}</ReactMarkdown>
                      </div>
                    ) : (
                      <div className="prose prose-sm max-w-none">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content}</ReactMarkdown>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
            {loading && (
              <div className="flex items-center gap-2 text-gray-300 text-sm">
                <Loader2 className="w-4 h-4 animate-spin" /> Thinking...
              </div>
            )}
          </div>

          {/* Input docked to viewport bottom (fixed) with compact helper row */}
          <div className="fixed inset-x-0 bottom-3 md:bottom-5 z-40 px-4 pb-3 md:pb-4">
            <div className="w-full md:w-2/3 max-w-none mx-auto flex items-center justify-center md:hidden mb-1">
              <button onClick={clearChat} className="text-[11px] underline text-gray-300 hover:text-white">Start a new chat</button>
            </div>
            <motion.div layoutId="chatInput" layout className={`glow-ring-box w-full md:w-2/3 max-w-none mx-auto transition-all ${inputTall ? 'rounded-2xl' : 'rounded-full'}`}>
              <motion.div layout className={`relative flex items-center gap-2 bg-gray-900/60 ${inputTall ? 'rounded-2xl' : 'rounded-full'} p-2 md:p-3 border border-white/10 backdrop-blur-md shadow-lg transition-all`}>
                <textarea
                  ref={textareaRef}
                  rows={1}
                  value={input}
                  onChange={(e) => { setInput(e.target.value); autoResize(e.currentTarget); }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      send();
                    }
                  }}
                  placeholder="Ask for recommendations..."
                  className={`flex-1 bg-transparent text-white ${inputTall ? 'rounded-2xl' : 'rounded-full'} px-5 py-1.5 md:py-2.5 outline-none placeholder:text-gray-500 resize-none overflow-hidden leading-6`}
                />
                <button
                  onClick={send}
                  className="p-2 md:p-2.5 text-[#c9fffc] hover:text-white disabled:opacity-40 self-center"
                  disabled={loading || !input.trim()}
                  aria-label="Send"
                  title="Send"
                >
                  <Send className="w-5 h-5" />
                </button>
              </motion.div>
            </motion.div>
            <div className="hidden md:flex mt-1 w-full md:w-2/3 max-w-none mx-auto items-center justify-center gap-3 px-1">
              <div className="text-[11px] leading-tight text-gray-400">Tip: Add goals or niche (e.g., "for creators", "for SaaS").</div>
              <button onClick={clearChat} className="text-[11px] leading-tight text-gray-400 hover:text-[#c9fffc] underline">Clear chat</button>
            </div>
          </div>
        </>
      )}

      {/* Hamburger morph removed per product decision */}
      </div>
    </LayoutGroup>
  );
}

// (deterministic markdown fallback removed per product decision)
